import { useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Upload, FileText, X } from 'lucide-react';

export const ImportModal = ({ open, onClose, onImport }) => {
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragIn = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  }, []);

  const handleDragOut = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const csvFile = files[0];
      if (csvFile.name.endsWith('.csv')) {
        setFile(csvFile);
      }
    }
  }, []);

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setFile(files[0]);
    }
  };

  const handleImport = async () => {
    if (!file) return;
    setLoading(true);
    try {
      await onImport(file);
      setFile(null);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Import Leads from CSV</DialogTitle>
          <DialogDescription>
            Upload a CSV file with your lead data. We'll automatically map common column names.
          </DialogDescription>
        </DialogHeader>

        <div
          onDragEnter={handleDragIn}
          onDragLeave={handleDragOut}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={`
            border-2 border-dashed rounded-lg p-8 text-center transition-colors
            ${dragging ? 'border-accent bg-accent/10' : 'border-border hover:border-muted-foreground'}
          `}
          data-testid="csv-dropzone"
        >
          {file ? (
            <div className="flex items-center justify-center gap-3">
              <FileText className="w-8 h-8 text-accent" />
              <div className="text-left">
                <p className="font-medium text-sm">{file.name}</p>
                <p className="text-xs text-muted-foreground">
                  {(file.size / 1024).toFixed(1)} KB
                </p>
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => setFile(null)}
                className="ml-2"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <>
              <Upload className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
              <p className="text-sm font-medium mb-1">Drop your CSV file here</p>
              <p className="text-xs text-muted-foreground mb-4">or click to browse</p>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="hidden"
                id="csv-input"
                data-testid="csv-file-input"
              />
              <label htmlFor="csv-input">
                <Button variant="outline" size="sm" asChild>
                  <span>Browse Files</span>
                </Button>
              </label>
            </>
          )}
        </div>

        <div className="text-xs text-muted-foreground space-y-1">
          <p><strong>Supported columns:</strong></p>
          <p>Business Name, Phone Number, E-Mail, Address, Website, Rating, Review, GBP Link, Claim Status, Suggested Services</p>
        </div>

        <div className="flex gap-2 mt-2">
          <Button 
            variant="outline" 
            onClick={handleClose} 
            className="flex-1"
            data-testid="cancel-import-btn"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleImport} 
            disabled={!file || loading}
            className="flex-1 bg-accent hover:bg-accent/90 text-accent-foreground"
            data-testid="confirm-import-btn"
          >
            {loading ? 'Importing...' : 'Import Leads'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
